/**
 * A single game: live board, scoreboard, payouts, tracker, and -- for the
 * owner -- the admin panel.
 */

import { useEffect, useState } from 'react';
import { useGameSocket } from '../hooks/useGameSocket';
import { SquaresGrid } from './SquaresGrid';
import { EarningsTracker, PayoutStrip, Scoreboard } from './Scoreboard';
import { AdminPanel } from './AdminPanel';
import { JoinFlow } from './JoinFlow';
import { Confetti } from './Confetti';
import { SetupChecklist } from './SetupChecklist';
import { squaresAvailable, squaresSold } from '../../shared/game-logic';
import { readableTeamColor } from '../../shared/team-colors';
import { formatKickoff } from '../lib/format';
import type { SessionUser } from './App';
import { APP_NAME } from '../../shared/branding';

interface Props {
  gameId: string;
  user: SessionUser;
}

export function GamePage({ gameId, user }: Props) {
  const { state, role, status, viewers, error, celebration, send, clearError } =
    useGameSocket(gameId);
  const [tab, setTab] = useState<'board' | 'admin'>('board');
  const [copied, setCopied] = useState(false);

  // Team colors drive the banner gradient and axis headers. They are passed
  // through readableTeamColor first: about a third of the library stores a
  // black or near-black primary, which is invisible on our background. The
  // stored value stays the true brand color; only the painted one is adjusted.
  useEffect(() => {
    if (!state) return;
    const root = document.documentElement;
    root.style.setProperty('--team-home', readableTeamColor(state.config.teams.home.color));
    root.style.setProperty('--team-away', readableTeamColor(state.config.teams.away.color));
    document.title = `${state.config.title} · ${APP_NAME}`;

    // Both are set on the document, not on this component's markup, so they
    // outlive it unless explicitly undone -- leaving the games list wearing the
    // last game's colours and title.
    return () => {
      root.style.removeProperty('--team-home');
      root.style.removeProperty('--team-away');
      document.title = APP_NAME;
    };
  }, [state]);

  useEffect(() => {
    if (!error) return;
    const timer = setTimeout(clearError, 5000);
    return () => clearTimeout(timer);
  }, [error, clearError]);

  if (!state) {
    return (
      <div className="empty-state">
        {status === 'closed' ? 'Disconnected.' : 'Loading game…'}
      </div>
    );
  }

  const isAdmin = role === 'admin';
  const mySquares = state.grid.filter((cell) => cell === user.id).length;

  return (
    <>
      <header className="banner">
        <h1>{state.config.title}</h1>
        <div className="banner-meta">
          {state.config.teams.home.name} vs {state.config.teams.away.name}
        </div>
        {/* Kickoff and venue come straight from the fixture; players opening an
            invite link mostly want to know when. */}
        {(formatKickoff(state.config.date) || state.config.location) && (
          <div className="banner-meta banner-when">
            {[formatKickoff(state.config.date), state.config.location]
              .filter(Boolean)
              .join(' · ')}
          </div>
        )}
      </header>

      <div className="row" style={{ marginBottom: 16 }}>
        <span className="status-pill">
          <span className={`status-dot ${status}`} aria-hidden="true" />
          {status === 'open' ? `${viewers} watching` : status}
        </span>
        {isAdmin && <span className="badge admin">Admin</span>}
        <div className="spacer" />
        <button
          type="button"
          onClick={async () => {
            await navigator.clipboard.writeText(window.location.href);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
          }}
        >
          {copied ? 'Copied' : 'Copy invite link'}
        </button>
        {isAdmin && (
          <div className="row" role="tablist" aria-label="View">
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'board'}
              className={tab === 'board' ? 'btn-primary' : ''}
              onClick={() => setTab('board')}
            >
              Board
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'admin'}
              className={tab === 'admin' ? 'btn-primary' : ''}
              onClick={() => setTab('admin')}
            >
              Manage
            </button>
          </div>
        )}
      </div>

      {tab === 'admin' && isAdmin ? (
        <AdminPanel state={state} send={send} />
      ) : (
        <div className="stack">
          {isAdmin && <SetupChecklist state={state} onManage={() => setTab('admin')} />}
          <Scoreboard state={state} />
          {role !== 'admin' && (
            <JoinFlow
              state={state}
              userId={user.id}
              defaultName={user.name}
              send={send}
            />
          )}
          <section className="card">
            <div className="row" style={{ marginBottom: 12 }}>
              <h2 className="card-title" style={{ margin: 0 }}>
                Board
              </h2>
              <div className="spacer" />
              {/* An empty grid says nothing on its own; the count is the thing
                  a player actually wants to know before asking for squares. */}
              <span className="small muted">
                {mySquares > 0 && (
                  <span className="mine-count">
                    {mySquares} yours
                  </span>
                )}
                {squaresAvailable(state) > 0
                  ? `${squaresAvailable(state)} of 100 open`
                  : 'All 100 taken'}
                {!state.axesRevealed && ' · numbers hidden'}
              </span>
            </div>
            <SquaresGrid state={state} assigningPlayerId={null} viewerId={user.id} />
          </section>
          <PayoutStrip state={state} />
          <EarningsTracker state={state} />
        </div>
      )}

      <Confetti trigger={celebration} />

      {error && (
        <div className="toast-stack">
          <div className="toast error" role="alert">
            {error}
          </div>
        </div>
      )}
    </>
  );
}
